/**
 * Actycrea: news.esm.js (ES6)
 * MIT
 *
 * Horizontal blog-post slider for the `module-news` layout: the previous/next
 * controls translate the track one card at a time with GSAP, and the track can
 * also be dragged ( pointer ) or swiped ( touch ).
 *
 * The card step is measured from the live DOM ( first card width + computed gap )
 * instead of being hardcoded, so the same code serves the three breakpoints
 * defined in _news.scss without duplicating the values here. Bounds are
 * recomputed on `onResizeEnd` ( see resize.esm.js ) and on window load, since
 * lazy-loaded featured images can change the track width after boot.
 *
 * Honors `prefers-reduced-motion`: when set, no GSAP tween is built and the
 * viewport falls back to native scroll snapping ( see _news.scss ).
 *
 * @module esm/news
 */
import { gsap } from 'gsap';
import { PREFIX } from '../helpers/config.js';

const ATTR_ROOT         = `data-${PREFIX}-news`;
const SELECTOR_DATA     = `[${ATTR_ROOT}]`;
const SELECTOR_PREV     = `[data-${PREFIX}-news-prev]`;
const SELECTOR_NEXT     = `[data-${PREFIX}-news-next]`;
const VIEWPORT_SELECTOR = '.news-viewport';
const TRACK_SELECTOR    = '.news-track';
const ITEM_SELECTOR     = '.news-item';

const DEFAULTS = {
  // Tween duration, in seconds, for a single card step.
  duration: 0.7 ,
  // Easing of the step. `power3.out` decelerates late, which reads as a
  // deliberate slide rather than a scroll jump.
  ease: 'power3.out' ,
  // Minimum pointer travel, in px, before a drag is treated as a swipe instead
  // of a click on the card link.
  dragThreshold: 12
};

export class News {
  /**
   * @param {HTMLElement} element - The module section root.
   * @param {Object}      config  - Overrides for DEFAULTS ( duration, ease ).
   */
  constructor( element , config = { } ){
    if( !(element instanceof HTMLElement) ) throw new Error( 'News: elemento inválido' );

    this._config   = { ...DEFAULTS , ...config };
    this._element  = element;
    this._viewport = element.querySelector( VIEWPORT_SELECTOR );
    this._track    = element.querySelector( TRACK_SELECTOR );
    this._prev     = element.querySelector( SELECTOR_PREV );
    this._next     = element.querySelector( SELECTOR_NEXT );

    /**
     * Current horizontal offset of the track, in px ( always <= 0 ).
     *
     * @type {number}
     */
    this._offset = 0;

    /**
     * Pointer drag state.
     *
     * @type {{ active: boolean , id: number , startX: number , startOffset: number , moved: boolean }}
     */
    this._drag = { active: false , id: -1 , startX: 0 , startOffset: 0 , moved: false };

    this._onPrev        = this._onPrev.bind( this );
    this._onNext        = this._onNext.bind( this );
    this._onPointerDown = this._onPointerDown.bind( this );
    this._onPointerMove = this._onPointerMove.bind( this );
    this._onPointerUp   = this._onPointerUp.bind( this );
    this._onDragStart   = this._onDragStart.bind( this );
    this._onClickGuard  = this._onClickGuard.bind( this );
    this._onResize      = this._onResize.bind( this );

    if( !this._viewport || !this._track ) return;

    this._bind( );
    this._sync( );
  }

  /**
   * Distance travelled by a single step: card width plus the gap between cards.
   *
   * @returns {number}
   */
  _step( ){
    const item = this._track.querySelector( ITEM_SELECTOR );
    if( !item ) return 0;

    const gap = parseFloat( window.getComputedStyle( this._track ).columnGap ) || 0;

    return item.getBoundingClientRect( ).width + gap;
  }

  /**
   * Inline gutter of the viewport ( CSS `--news-gutter`, applied as its
   * `padding-left` ). Read from the computed style so the breakpoint ramp
   * defined in _news.scss stays the single source of truth.
   *
   * @returns {number}
   */
  _gutter( ){
    return parseFloat( window.getComputedStyle( this._viewport ).paddingLeft ) || 0;
  }

  /**
   * Total inline extent of the cards, measured from the right edge of the last
   * one.
   *
   * @returns {number}
   */
  _contentWidth( ){
    const last = this._track.lastElementChild;

    if( !last ) return 0;

    // `offsetLeft` / `offsetWidth` are layout values, so they ignore the GSAP
    // transform on the track and can be read at any point of a tween. They are
    // relative to the track itself because _news.scss makes it `relative`.
    // `scrollWidth` was used here before and under-reported the extent of this
    // non-scrolling flex track, which left the last card clipped.
    return last.offsetLeft + last.offsetWidth;
  }

  /**
   * Maximum negative offset: how far the track has to travel for the last card
   * to be fully revealed.
   *
   * `clientWidth` includes the viewport padding, so the space actually
   * available for cards is the content box, gutter excluded.
   *
   * A trailing gutter of the same size is then added, so reaching the end
   * leaves the last card with the same breathing room the first one has on the
   * left instead of sitting flush against the edge.
   *
   * @returns {number}
   */
  _maxOffset( ){
    const gutter    = this._gutter( );
    const available = this._viewport.clientWidth - gutter;
    const overflow  = this._contentWidth( ) - available;

    // The whole track already fits: no travel at all. Returning early also
    // avoids the trailing gutter turning a perfect fit into a few pointless
    // pixels of scroll with the controls enabled.
    if( overflow <= 0 ){
      return 0;
    }

    return overflow + gutter;
  }

  /**
   * Clamps an offset into the valid [ -maxOffset , 0 ] range.
   *
   * @param   {number} value
   * @returns {number}
   */
  _clamp( value ){
    return Math.min( 0 , Math.max( -this._maxOffset( ) , value ) );
  }

  /**
   * Moves the track by `direction` cards.
   *
   * @param   {number} direction - 1 forwards, -1 backwards.
   * @returns {void}
   */
  _move( direction ){
    this._to( this._offset - ( direction * this._step( ) ) );
  }

  /**
   * Tweens the track to an absolute offset and refreshes the controls.
   *
   * @param   {number} value
   * @returns {void}
   */
  _to( value ){
    this._offset = this._clamp( value );

    gsap.to( this._track , {
      x: this._offset ,
      duration: this._config.duration ,
      ease: this._config.ease ,
      overwrite: true
    } );

    this._updateControls( );
  }

  /**
   * Re-clamps the current offset ( after a resize or a late image load ) and
   * repositions the track without animating.
   *
   * @returns {void}
   */
  _sync( ){
    this._offset = this._clamp( this._offset );

    gsap.set( this._track , { x: this._offset } );

    this._updateControls( );
  }

  /**
   * Enables or disables the controls depending on the position in the track.
   * A track that fits the viewport disables both.
   *
   * @returns {void}
   */
  _updateControls( ){
    const max = this._maxOffset( );
    // Sub-pixel layout rounding would otherwise leave the last card one
    // fraction of a pixel short and keep "next" enabled forever.
    const atStart = this._offset >= -1;
    const atEnd   = this._offset <= ( -max + 1 );

    if( this._prev ) this._prev.disabled = atStart;
    if( this._next ) this._next.disabled = max === 0 || atEnd;
  }

  /**
   * @returns {void}
   */
  _onPrev( ){
    this._move( -1 );
  }

  /**
   * @returns {void}
   */
  _onNext( ){
    this._move( 1 );
  }

  /**
   * Starts a drag gesture.
   *
   * @param   {PointerEvent} event
   * @returns {void}
   */
  _onPointerDown( event ){
    if( event.button !== undefined && event.button !== 0 ) return;
    if( this._maxOffset( ) === 0 ) return;

    // NOTE: pointer capture is deliberately NOT taken here. With an active
    // capture the browser retargets the resulting `click` to the capturing
    // element, so it would never reach the card's `<a>` and the post would
    // become unreachable. It is claimed in `_onPointerMove`, once the gesture
    // is confirmed to be a drag and no navigation is expected.
    this._drag = {
      active: true ,
      id: event.pointerId ,
      startX: event.clientX ,
      startOffset: this._offset ,
      moved: false
    };
  }

  /**
   * Follows the pointer while dragging.
   *
   * @param   {PointerEvent} event
   * @returns {void}
   */
  _onPointerMove( event ){
    if( !this._drag.active || event.pointerId !== this._drag.id ) return;

    const delta = event.clientX - this._drag.startX;

    if( !this._drag.moved && Math.abs( delta ) < this._config.dragThreshold ) return;

    // Threshold crossed: this is a drag, not a click. Only now is the pointer
    // captured, so the gesture survives the pointer leaving the viewport while
    // a plain click keeps its original target ( the card link ).
    if( !this._drag.moved ){
      this._drag.moved = true;
      this._viewport.setPointerCapture?.( this._drag.id );
    }

    this._offset = this._clamp( this._drag.startOffset + delta );

    gsap.set( this._track , { x: this._offset } );
    this._updateControls( );
  }

  /**
   * Ends the drag and snaps to the nearest card boundary.
   *
   * @param   {PointerEvent} event
   * @returns {void}
   */
  _onPointerUp( event ){
    if( !this._drag.active || event.pointerId !== this._drag.id ) return;

    const moved = this._drag.moved;
    this._drag.active = false;

    // Only held when the gesture became a drag ( see `_onPointerMove` ).
    if( this._viewport.hasPointerCapture?.( event.pointerId ) ){
      this._viewport.releasePointerCapture( event.pointerId );
    }

    if( !moved ) return;

    const step = this._step( );

    if( step <= 0 ){
      this._sync( );
      return;
    }

    const max     = this._maxOffset( );
    const snapped = this._clamp( Math.round( this._offset / step ) * step );

    // The end of the track is a snap point in its own right: the remaining
    // travel is almost never an exact multiple of a card, so rounding alone
    // always stopped short and left the last card partially hidden. Settle on
    // whichever of the two candidates the drag actually ended closer to.
    const toEnd     = Math.abs( this._offset + max );
    const toSnapped = Math.abs( this._offset - snapped );

    this._to( toEnd < toSnapped ? -max : snapped );
  }

  /**
   * Cancels the browser's native drag-and-drop.
   *
   * Every card is an `<a>` wrapping an `<img>`, and both are natively
   * draggable: without this the browser starts its own link/image drag as soon
   * as the pointer moves, which fires `pointercancel` and kills the swipe. It
   * is the reason dragging only used to work on the blank gaps between cards.
   *
   * @param   {DragEvent} event
   * @returns {void}
   */
  _onDragStart( event ){
    event.preventDefault( );
  }

  /**
   * Swallows the click that closes a drag gesture, so swiping the track never
   * navigates to the card permalink.
   *
   * @param   {MouseEvent} event
   * @returns {void}
   */
  _onClickGuard( event ){
    if( !this._drag.moved ) return;

    event.preventDefault( );
    event.stopPropagation( );
    this._drag.moved = false;
  }

  /**
   * @returns {void}
   */
  _onResize( ){
    this._sync( );
  }

  /**
   * Wires the controls, the drag gesture and the recalculation listeners.
   *
   * @returns {void}
   */
  _bind( ){
    this._prev?.addEventListener( 'click' , this._onPrev );
    this._next?.addEventListener( 'click' , this._onNext );

    this._viewport.addEventListener( 'pointerdown' , this._onPointerDown );
    this._viewport.addEventListener( 'pointermove' , this._onPointerMove );
    this._viewport.addEventListener( 'pointerup' , this._onPointerUp );
    this._viewport.addEventListener( 'pointercancel' , this._onPointerUp );
    this._viewport.addEventListener( 'dragstart' , this._onDragStart );
    this._viewport.addEventListener( 'click' , this._onClickGuard , true );

    window.addEventListener( 'onResizeEnd' , this._onResize );

    if( document.readyState === 'complete' ){
      this._sync( );
    } else {
      window.addEventListener( 'load' , this._onResize , { once: true } );
    }
  }

  /**
   * Removes every listener and kills the pending tween.
   *
   * @returns {void}
   */
  destroy( ){
    gsap.killTweensOf( this._track );

    this._prev?.removeEventListener( 'click' , this._onPrev );
    this._next?.removeEventListener( 'click' , this._onNext );

    this._viewport?.removeEventListener( 'pointerdown' , this._onPointerDown );
    this._viewport?.removeEventListener( 'pointermove' , this._onPointerMove );
    this._viewport?.removeEventListener( 'pointerup' , this._onPointerUp );
    this._viewport?.removeEventListener( 'pointercancel' , this._onPointerUp );
    this._viewport?.removeEventListener( 'dragstart' , this._onDragStart );
    this._viewport?.removeEventListener( 'click' , this._onClickGuard , true );

    window.removeEventListener( 'onResizeEnd' , this._onResize );
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    // Respect the user's motion preference: leave the native scroll fallback.
    if( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ){
      return [ ];
    }

    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];
    nodes.forEach( ( el ) => {
      if( el._newsInstance ) return;
      el._newsInstance = new News( el );
      instances.push( el._newsInstance );
    } );

    return instances;
  }
}

export default News;
